import React, { useState, useRef, useCallback } from 'react';
import { useAppStore, ALL_PROPS, KeyframeProp, LayerKeyframes } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { SelectedLayerKf, LayerKfClipboardItem } from '../components/timeline/timelineConstants';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Describes one visible row in the track area, top/height in scroll-container pixels. */
export interface RowDescriptor {
  layerId: string;
  /** null = the collapsed/summary row (selects all props); a prop key = one sub-row */
  prop: KeyframeProp | null;
  top: number;
  height: number;
}

interface Options {
  trackScrollRef:      React.RefObject<HTMLDivElement | null>;
  pxPerFrame:          number;
  totalFrames:         number;
  removeAllKfsAtFrame: (layerId: string, frame: number) => void;
  /** Current row layout; kept as a ref so closures always see the latest value. */
  rowLayoutRef:        React.MutableRefObject<RowDescriptor[]>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
// Owns: selectedKfs, regionSelect, kfClipboard, frameFromTrack,
//       region pointer handlers, clipboard ops, sub-component wrapper callbacks.

export function useKfSelection({
  trackScrollRef,
  pxPerFrame,
  totalFrames,
  removeAllKfsAtFrame,
  rowLayoutRef,
}: Options) {
  const setCurrentFrame = useAppStore((s) => s.setCurrentFrame);
  const isPlaying       = useAppStore((s) => s.isPlaying);
  const setIsPlaying    = useAppStore((s) => s.setIsPlaying);
  const setKeyframe     = useAppStore((s) => s.setKeyframe);
  const removeKeyframe  = useAppStore((s) => s.removeKeyframe);

  const [selectedKfs, setSelectedKfs]   = useState<SelectedLayerKf[]>([]);
  const [regionSelect, setRegionSelect] = useState<{
    startFrame: number; endFrame: number;
    startY:     number; endY:     number;
  } | null>(null);

  const regionDragRef = useRef<{
    startFrame: number; endFrame: number;
    startY:     number; endY:     number;
    moved:      boolean;
  } | null>(null);

  // Stable refs so document-level closures always see the latest values.
  const pxPerFrameRef   = useRef(pxPerFrame);
  pxPerFrameRef.current = pxPerFrame;
  const totalFramesRef   = useRef(totalFrames);
  totalFramesRef.current = totalFrames;

  const kfClipboardRef = useRef<LayerKfClipboardItem[]>([]);

  // ── frameFromTrack ───────────────────────────────────────────────────────

  const frameFromTrack = useCallback(
    (e: { clientX: number }) => {
      const el = trackScrollRef.current;
      if (!el) return 0;
      const x = e.clientX - el.getBoundingClientRect().left + el.scrollLeft;
      return Math.max(0, Math.min(totalFrames - 1, Math.round(x / pxPerFrame)));
    },
    [pxPerFrame, totalFrames, trackScrollRef]
  );

  // ── Cross-layer range selection ──────────────────────────────────────────

  const clearSelectedKfs = useCallback(() => { setSelectedKfs([]); }, []);

  /**
   * Collect keyframes that fall inside [minFrame, maxFrame] × [minY, maxY]
   * (Y measured from the top of the scrollable track container).
   *
   * A row is "hit" when its vertical midpoint falls inside [minY, maxY].
   * A null-prop (summary) row selects all properties; a named-prop row
   * selects only that property.
   */
  const collectSelectionInRange = useCallback(
    (startFrame: number, endFrame: number, startY: number, endY: number): SelectedLayerKf[] => {
      const minF = Math.min(startFrame, endFrame);
      const maxF = Math.max(startFrame, endFrame);
      const minY = Math.min(startY, endY);
      const maxY = Math.max(startY, endY);

      // Use getState() so the closure is never stale even if layerKeyframes
      // changed since the last React render.
      const kfState = useAppStore.getState().layerKeyframes;
      const out: SelectedLayerKf[] = [];
      const seen = new Set<string>();

      for (const row of rowLayoutRef.current) {
        // A row is covered when its vertical midpoint is within the Y band.
        const rowMid = row.top + row.height / 2;
        if (rowMid < minY || rowMid > maxY) continue;

        const kfs = kfState[row.layerId];
        if (!kfs) continue;

        const propsToCheck: KeyframeProp[] = row.prop !== null ? [row.prop] : ALL_PROPS;
        for (const prop of propsToCheck) {
          for (const frameStr of Object.keys(kfs[prop])) {
            const frame = Number(frameStr);
            if (frame >= minF && frame <= maxF) {
              const key = `${row.layerId}|${prop}|${frame}`;
              if (!seen.has(key)) {
                seen.add(key);
                out.push({ layerId: row.layerId, prop, frame });
              }
            }
          }
        }
      }
      return out;
    },
    [] // deliberate empty deps — uses refs + getState() so always fresh
  );

  // ── Region selection ─────────────────────────────────────────────────────
  //
  // We do NOT use pointer capture so that child onClick / onDoubleClick
  // handlers (add/remove kf, set frame) continue to work normally.
  // Kf drag elements call e.stopPropagation() so they never start a region drag.
  // Document-level listeners guarantee we get move/up even if the pointer
  // leaves the scroll container.

  const handleRegionPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      // Kf elements call stopPropagation, but guard here as a safety net.
      if ((e.target as HTMLElement).dataset.kf) return;

      const el = trackScrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x  = e.clientX - rect.left + el.scrollLeft;
      const y  = e.clientY - rect.top  + el.scrollTop;
      const frame = Math.max(0, Math.min(totalFramesRef.current - 1, Math.round(x / pxPerFrameRef.current)));

      regionDragRef.current = { startFrame: frame, endFrame: frame, startY: y, endY: y, moved: false };
      setRegionSelect({ startFrame: frame, endFrame: frame, startY: y, endY: y });
      setCurrentFrame(frame);
      if (isPlaying) setIsPlaying(false);

      const onMove = (ev: PointerEvent) => {
        const drag = regionDragRef.current;
        if (!drag) return;
        const container = trackScrollRef.current;
        if (!container) return;
        const r = container.getBoundingClientRect();
        const nx  = ev.clientX - r.left + container.scrollLeft;
        const ny  = ev.clientY - r.top  + container.scrollTop;
        const nf  = Math.max(0, Math.min(totalFramesRef.current - 1, Math.round(nx / pxPerFrameRef.current)));
        if (nf === drag.endFrame && Math.abs(ny - drag.endY) < 1) return;
        drag.endFrame = nf;
        drag.endY     = ny;
        drag.moved    = true;
        regionDragRef.current = { ...drag };
        setRegionSelect({ startFrame: drag.startFrame, endFrame: nf, startY: drag.startY, endY: ny });
        // collectSelectionInRange uses refs — safe to call inside a stable closure.
        setSelectedKfs(collectSelectionInRange(drag.startFrame, nf, drag.startY, ny));
      };

      const onUp = () => {
        const drag = regionDragRef.current;
        if (drag && !drag.moved) setSelectedKfs([]);
        regionDragRef.current = null;
        setRegionSelect(null);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup',   onUp);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup',   onUp);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectSelectionInRange, isPlaying, setCurrentFrame, setIsPlaying, trackScrollRef]
  );

  // ── Clipboard ────────────────────────────────────────────────────────────

  const copySelectedKfs = useCallback(() => {
    if (!selectedKfs.length) return;
    const minFrame = Math.min(...selectedKfs.map((k) => k.frame));
    const nextClipboard: LayerKfClipboardItem[] = [];
    for (const kf of selectedKfs) {
      const layerKfs = useAppStore.getState().layerKeyframes[kf.layerId];
      if (!layerKfs || !(kf.frame in layerKfs[kf.prop])) continue;
      const value = (layerKfs[kf.prop] as Record<number, boolean | number>)[kf.frame];
      nextClipboard.push({ layerId: kf.layerId, prop: kf.prop, frameOffset: kf.frame - minFrame, value });
    }
    kfClipboardRef.current = nextClipboard;
  }, [selectedKfs]);

  const pasteSelectedKfsAtFrame = useCallback(
    (targetFrame: number) => {
      const clip = kfClipboardRef.current;
      if (!clip.length) return;
      const targetLayerId = useAppStore.getState().selectedLayerId ?? clip[0].layerId;
      viewportActions.pushSnapshot.current();
      const nextSelection: SelectedLayerKf[] = [];
      for (const item of clip) {
        const frame = Math.max(0, Math.min(totalFrames - 1, targetFrame + item.frameOffset));
        setKeyframe(targetLayerId, item.prop, frame, item.value);
        nextSelection.push({ layerId: targetLayerId, prop: item.prop, frame });
      }
      setSelectedKfs(nextSelection);
      setCurrentFrame(targetFrame);
    },
    [setCurrentFrame, setKeyframe, totalFrames]
  );

  const deleteSelectedKfs = useCallback(() => {
    if (!selectedKfs.length) return;
    viewportActions.pushSnapshot.current();
    for (const kf of selectedKfs) removeKeyframe(kf.layerId, kf.prop, kf.frame);
    setSelectedKfs([]);
  }, [removeKeyframe, selectedKfs]);

  // ── Sub-component wrapper callbacks ──────────────────────────────────────

  const handleRemoveAllKfsAndDeselect = useCallback((layerId: string, frame: number) => {
    removeAllKfsAtFrame(layerId, frame);
    setSelectedKfs((prev) => prev.filter((k) => !(k.layerId === layerId && k.frame === frame)));
  }, [removeAllKfsAtFrame]);

  const handleSummaryKfClick = useCallback(
    (layerId: string, frame: number, kfs: LayerKeyframes) => {
      setCurrentFrame(frame);
      const picked = ALL_PROPS.filter((p) => frame in kfs[p]).map((p) => ({ layerId, prop: p, frame }));
      setSelectedKfs(picked);
    },
    [setCurrentFrame]
  );

  const handlePropKfClick = useCallback((layerId: string, prop: KeyframeProp, frame: number) => {
    setCurrentFrame(frame);
    setSelectedKfs([{ layerId, prop, frame }]);
  }, [setCurrentFrame]);

  const handleRemovePropKfWithSnapshot = useCallback(
    (layerId: string, prop: KeyframeProp, frame: number) => {
      viewportActions.pushSnapshot.current();
      removeKeyframe(layerId, prop, frame);
      setSelectedKfs((prev) => prev.filter((k) => !(k.layerId === layerId && k.prop === prop && k.frame === frame)));
    },
    [removeKeyframe]
  );

  return {
    selectedKfs,
    setSelectedKfs,
    regionSelect,
    kfClipboardRef,
    frameFromTrack,
    clearSelectedKfs,
    copySelectedKfs,
    pasteSelectedKfsAtFrame,
    deleteSelectedKfs,
    handleRegionPointerDown,
    handleRemoveAllKfsAndDeselect,
    handleSummaryKfClick,
    handlePropKfClick,
    handleRemovePropKfWithSnapshot,
  };
}

